<template>
  <div class="invoice-generation-days-container">
    <!-- Error Alert -->
    <b-alert
      v-if="error"
      variant="danger"
      show
      dismissible
      @dismissed="error = null"
    >
      <feather-icon
        icon="AlertTriangleIcon"
        size="16"
        class="mr-1"
      />
      {{ error }}
    </b-alert>

    <b-overlay
      :show="isLoading"
      spinner-variant="primary"
      rounded
    >
      <!-- Header -->
      <div class="mb-4">
        <h5 class="mb-2">
          <feather-icon
            icon="CalendarIcon"
            size="20"
            class="text-primary mr-1"
          />
          Jours de génération de facture
        </h5>
        <p class="text-muted">
          Sélectionnez les jours de la semaine pour la génération automatique des factures pour ce fournisseur.
        </p>
      </div>

      <!-- Days Grid -->
      <b-row class="days-grid">
        <b-col
          v-for="day in weekdays"
          :key="day.value"
          cols="6"
          md="4"
          lg="3"
          class="mb-3"
        >
          <b-card
            :class="[
              'day-card',
              'text-center',
              'cursor-pointer',
              { 'selected': isSelected(day.value) }
            ]"
            @click="toggleDay(day.value)"
          >
            <div class="day-icon mb-2">
              <feather-icon
                v-if="isSelected(day.value)"
                icon="CheckCircleIcon"
                size="32"
                class="text-success"
              />
              <feather-icon
                v-else
                icon="CircleIcon"
                size="32"
                class="text-muted"
              />
            </div>
            <h6 class="mb-0">
              {{ day.label }}
            </h6>
          </b-card>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end mt-4">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="resetSelection"
        >
          <feather-icon
            icon="XIcon"
            size="16"
            class="mr-1"
          />
          Réinitialiser
        </b-button>
        <b-button
          variant="primary"
          :disabled="isLoading"
          @click="saveDays"
        >
          <feather-icon
            icon="SaveIcon"
            size="16"
            class="mr-1"
          />
          Enregistrer
        </b-button>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BAlert,
} from 'bootstrap-vue'

export default {
  name: 'InvoiceGenerationDays',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BAlert,
  },
  props: {
    supplierId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      error: null,
      selectedDays: [],
      originalDays: [],
      weekdays: [
        { value: 'monday', label: 'Lundi' },
        { value: 'tuesday', label: 'Mardi' },
        { value: 'wednesday', label: 'Mercredi' },
        { value: 'thursday', label: 'Jeudi' },
        { value: 'friday', label: 'Vendredi' },
        { value: 'saturday', label: 'Samedi' },
        { value: 'sunday', label: 'Dimanche' },
      ],
    }
  },
  mounted() {
    this.fetchInvoiceGenerationDays()
  },
  methods: {
    async fetchInvoiceGenerationDays() {
      this.isLoading = true
      this.error = null
      try {
        const { data } = await axios.get(
          `/api/auth/users/suppliers/${this.supplierId}/invoice-generation-days/`,
        )
        this.selectedDays = [...(data.invoice_generation_days || [])]
        this.originalDays = [...(data.invoice_generation_days || [])]
      } catch (error) {
        this.error = error.response?.data?.detail || 'Impossible de charger les jours de génération de facture'
        this.showToast(
          'danger',
          'Erreur',
          this.error,
        )
      } finally {
        this.isLoading = false
      }
    },

    isSelected(day) {
      return this.selectedDays.includes(day)
    },

    toggleDay(day) {
      const index = this.selectedDays.indexOf(day)
      if (index > -1) {
        this.selectedDays.splice(index, 1)
      } else {
        this.selectedDays.push(day)
      }
    },

    resetSelection() {
      this.selectedDays = [...this.originalDays]
    },

    async saveDays() {
      this.isLoading = true
      this.error = null
      try {
        const { data } = await axios.patch(
          `/api/auth/users/suppliers/${this.supplierId}/invoice-generation-days/update/`,
          {
            invoice_generation_days: this.selectedDays,
          },
        )
        this.originalDays = [...this.selectedDays]
        this.$emit('saved', [...this.selectedDays])
        this.showToast(
          'success',
          'Succès',
          data.message || 'Jours de génération de facture mis à jour avec succès',
        )
      } catch (error) {
        const errorMsg = error.response?.data?.detail || 'Impossible de sauvegarder les modifications'
        this.error = errorMsg
        this.showToast(
          'danger',
          'Erreur',
          errorMsg,
        )
      } finally {
        this.isLoading = false
      }
    },

    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.invoice-generation-days-container {
  padding: 1.5rem;
}

.days-grid {
  margin: 0 -0.5rem;
}

.day-card {
  transition: all 0.3s ease;
  border: 2px solid #e9ecef;
  cursor: pointer;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #C62828;
  }

  &.selected {
    border-color: #28c76f;
    background: linear-gradient(135deg, rgba(40, 199, 111, 0.05) 0%, rgba(40, 199, 111, 0.1) 100%);

    &:hover {
      border-color: #28c76f;
      box-shadow: 0 4px 12px rgba(40, 199, 111, 0.3);
    }
  }

  .card-body {
    padding: 1.5rem 1rem;
  }
}

.day-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
